/**
 * Centralized analytics tracker — fires events to both GA4 and Meta Pixel
 * when configured. Falls back silently when ID env vars are missing.
 *
 * Consent: gtag Consent Mode v2 controls whether GA stores cookies. Meta Pixel
 * only initializes AFTER user consent (see index.html + CookieConsentBanner).
 *
 * Usage:
 *   import { trackEvent, trackConversion } from "@/lib/analytics";
 *   trackEvent("guardian_cta_clicked", { source: "donor_thanks_email" });
 *   trackConversion("donation_completed", { value: 250, currency: "TRY" });
 */

const isGaReady = () => typeof window !== "undefined" && typeof window.gtag === "function";
const isFbqReady = () => typeof window !== "undefined" && typeof window.fbq === "function";

/**
 * Standard event tracking — sent to both GA4 (custom event) and Meta Pixel (CustomEvent).
 * Use this for UI interactions: clicks, opens, form submits.
 */
export function trackEvent(eventName, params = {}) {
  try {
    if (isGaReady()) window.gtag("event", eventName, params);
    if (isFbqReady()) window.fbq("trackCustom", eventName, params);
  } catch (e) {
    // never let analytics break the app
    console.warn("[analytics] trackEvent failed:", e);
  }
}

/**
 * Conversion event — for revenue-bearing actions (donation, guardian signup).
 * Maps to GA4 'purchase' (with value) and Meta Pixel standard 'Donate'/'Purchase'.
 */
export function trackConversion(eventName, params = {}) {
  try {
    const { value, currency = "TRY", ...rest } = params;
    if (isGaReady()) {
      window.gtag("event", eventName, { value, currency, ...rest });
    }
    if (isFbqReady()) {
      // map known conversion event names → Meta standard events
      const META_STANDARD = {
        donation_completed: "Donate",
        guardian_signup_completed: "Subscribe",
        adoption_completed: "Lead",
        donor_thanks_viewed: "Lead",
      };
      const metaEvent = META_STANDARD[eventName] || "Lead";
      window.fbq("track", metaEvent, { value, currency, ...rest });
      // ek olarak custom name'i de gönder (raporda eşleştirme için)
      window.fbq("trackCustom", eventName, { value, currency, ...rest });
    }
  } catch (e) {
    console.warn("[analytics] trackConversion failed:", e);
  }
}

/**
 * Funnel step tracker — for the intermediate stages between arrival and conversion.
 * GA4 receives custom event; Meta Pixel receives its standard funnel equivalent
 * (ViewContent / InitiateCheckout / AddToWishlist) so Ads Manager can build
 * lookalike audiences correctly.
 *
 * step ∈ 'view_dog' | 'begin_adoption' | 'begin_donation' | 'begin_guardian'
 */
export function trackFunnelStep(step, params = {}) {
  try {
    if (isGaReady()) window.gtag("event", step, params);
    if (isFbqReady()) {
      const META_MAP = {
        view_dog: "ViewContent",
        begin_adoption: "InitiateCheckout",
        begin_donation: "InitiateCheckout",
        begin_guardian: "InitiateCheckout",
      };
      const metaEvent = META_MAP[step];
      if (metaEvent) {
        window.fbq("track", metaEvent, params);
      }
      // Always mirror the custom name so we can filter by step in Ads Manager.
      window.fbq("trackCustom", step, params);
    }
  } catch (e) {
    console.warn("[analytics] trackFunnelStep failed:", e);
  }
}

/**
 * SPA page view tracker. Call on route change.
 * GA4 already tracks automatic page_view; this is for manual SPA control.
 */
export function trackPageView(path) {
  try {
    if (isGaReady()) {
      window.gtag("event", "page_view", { page_path: path });
    }
    if (isFbqReady()) {
      window.fbq("track", "PageView");
    }
  } catch (e) {
    console.warn("[analytics] trackPageView failed:", e);
  }
}

/**
 * Consent status API — used by CookieConsentBanner.
 * setConsent("granted"|"denied") updates Consent Mode v2 + persists + toggles Meta Pixel.
 * hasConsentChoice() returns true if user made an explicit decision.
 */
const CONSENT_KEY = "kahu_cookie_consent";

export function hasConsentChoice() {
  if (typeof window === "undefined") return true;
  try {
    return !!window.localStorage.getItem(CONSENT_KEY);
  } catch (e) {
    return true; // If storage is blocked assume already-decided (do not show banner).
  }
}

export function getConsentStatus() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(CONSENT_KEY);
  } catch (e) {
    return null;
  }
}

export function setConsent(status) {
  if (status !== "granted" && status !== "denied") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, status);
  } catch (e) { /* storage blocked */ }

  // Update Google Consent Mode v2
  if (isGaReady()) {
    const val = status === "granted" ? "granted" : "denied";
    window.gtag("consent", "update", {
      ad_storage: val,
      ad_user_data: val,
      ad_personalization: val,
      analytics_storage: val,
    });
  }

  // Meta Pixel: init on grant. There is no post-init opt-out API — user rejection
  // just prevents the script from ever loading (privacy-first default).
  if (status === "granted" && typeof window.__kahuInitMetaPixel === "function") {
    window.__kahuInitMetaPixel();
  }
}
